import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Icons } from '@/assets/icons/icons';
import { backendApis } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { OTPCodeInput } from '@/components/ui/OTPCodeInput';
import { cn } from '@/lib/utils';
import { logoutUser } from '@/utils/auth';
import { classifyOtpError } from '@/utils/otp-errors';

export const Route = createFileRoute('/change-email/verify')({
  component: ChangeEmailVerifyScreen,
  staticData: { requiresAuth: true },
  async beforeLoad({ context, location }) {
    await context.auth.requireAuth(location);
  },
  validateSearch: z.object({
    email: z.string().email(),
  }).parse,
});

function ChangeEmailVerifyScreen() {
  const navigate = useNavigate();
  const { email } = Route.useSearch();
  const [code, setCode] = useState('');
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  const { mutate: verifyEmailChange } = useMutation({
    mutationKey: ['verify-email-change'],
    mutationFn: async ({
      otp,
      newEmail,
    }: {
      otp: string;
      newEmail: string;
    }) => {
      return backendApis.user.verifyEmailChange(otp, newEmail);
    },
    onSuccess: async () => {
      toast.success('Email updated', {
        description: 'Please sign in again with your new email',
      });
      await logoutUser();
      navigate({ to: '/login', replace: true });
    },
    onError: (error: unknown) => {
      setIsSubmittingCode(false);
      const { isInvalidCode, userMessage } = classifyOtpError(error);
      if (isInvalidCode) {
        setCode('');
        setErrorCount((c) => c + 1);
        toast.error("That code didn't work", { description: userMessage });
      } else {
        toast.error("Couldn't verify the code", {
          description:
            userMessage ||
            'Something went wrong. Please try again in a moment.',
        });
      }
    },
  });

  const { mutate: resendOTP } = useMutation({
    mutationKey: ['resend-email-change-otp'],
    mutationFn: async (newEmail: string) => {
      return backendApis.user.resendEmailChangeOTP(newEmail);
    },
    onSuccess: () => {
      setIsResending(false);
      toast.success('New code sent', {
        description: `Check ${email} for a fresh code.`,
      });
    },
    onError: (error: any) => {
      setIsResending(false);
      toast.error("Couldn't send a new code", {
        description:
          error?.response?.data?.detail?.user_error_message || error?.message,
      });
    },
  });

  const isLoading = isSubmittingCode || isResending;

  const handleSubmitCode = useCallback(
    async (_code: string) => {
      if (isLoading) return;
      setIsSubmittingCode(true);
      verifyEmailChange({ otp: _code, newEmail: email });
    },
    [email, isLoading, verifyEmailChange]
  );

  const handleResend = useCallback(() => {
    if (isLoading) return;
    setIsResending(true);
    setCode('');
    resendOTP(email);
  }, [email, isLoading, resendOTP]);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center',
        'bg-gradient-to-t from-background to-background-subtle',
        'h-screen',
        'overflow-y-auto',
        'relative',
        'drag-region'
      )}
    >
      <div className="flex flex-col w-full max-w-md px-4 sm:px-0 mb-16 z-10 relative">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'flex flex-row gap-1 items-center',
            'fixed top-10 left-4'
          )}
          onClick={() => navigate({ to: '/' })}
        >
          <Icons.chevronLeft className="size-4" />
          <span className="text-sm">Cancel</span>
        </Button>

        <div className="flex flex-col gap-8 justify-center">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col text-center items-center gap-2">
              <span className="text-xl font-medium text-foreground">
                Verify your new email
              </span>
              <p className="text-sm text-foreground-muted">
                Enter the code we sent to {email}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-6 items-center">
            <OTPCodeInput
              value={code}
              onChange={setCode}
              onComplete={handleSubmitCode}
              disabled={isLoading}
              transform={(raw) => raw.toUpperCase()}
              shakeKey={errorCount}
            />

            <p className="text-center text-foreground-muted text-sm">
              Didn't get it?{' '}
              <button
                type="button"
                className="text-accent font-medium cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleResend}
                disabled={isLoading}
              >
                {isResending ? 'Resending…' : 'Resend'}
              </button>
            </p>

            <Button
              className="max-w-64 w-full"
              type="submit"
              size="lg"
              disabled={code.length !== 6 || isLoading}
              onClick={() => handleSubmitCode(code)}
              isLoading={isSubmittingCode}
            >
              Verify
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
